'use client'
import { useMemo } from 'react'

import { useConfigContext } from '@/context/ConfigProvider'
import { eGeneralInfo, languages } from '@/types/enum'
import { cn, formatCurrency } from '@/lib/utils'

interface CurrencyProps {
  currency: number
  className?: string
  options?: { symbol?: string; showDecimal?: boolean; separator?: string; lang?: languages; showSymbol?: boolean }
  lang: string
  quantity?: number
  extraPrice?: number
  discount?: boolean
  orderCurrency?: number
}

export default function Currency({ currency, className, options, lang, quantity, extraPrice, discount = false, orderCurrency = 1 }: CurrencyProps) {
  const { getBasic } = useConfigContext()
  const q = quantity ?? 1
  const rate = useMemo(() => getBasic(eGeneralInfo.exchange_rate) ?? 1, [getBasic])
  const baseCurrency = currency ?? 0
  const extra_price = extraPrice ?? 0

  const vndCurr = useMemo(() => baseCurrency * q + extra_price, [baseCurrency, q, extra_price])
  const engCurr = useMemo(() => +((baseCurrency / +rate) * q + extra_price / +rate), [baseCurrency, rate, q, extra_price])

  const isVietnamese = lang === languages.vi
  const isVietNamDong = useConfigContext().currency === 'VND'
  const matchCurrency = useMemo(() => {
    const curr = isVietNamDong ? vndCurr : engCurr
    // Làm tròn đến 4 chữ số thập phân nếu giá trị nhỏ hơn 0.05, ngược lại làm tròn đến 3 chữ số
    const precision = curr < 0.05 ? 4 : 3
    const factor = Math.pow(10, precision)
    return Math.round(curr * factor) / factor
  }, [isVietNamDong, vndCurr, engCurr])

  const formattedCurrency = useMemo(() => {
    const value = matchCurrency === Infinity ? 0 : matchCurrency
    return formatCurrency(value, { lang: lang as languages, ...options, separator: '.', curencyType: isVietNamDong ? 'VND' : 'USD' }) ?? 0
  }, [matchCurrency, lang, options])

  if (orderCurrency === 2) {
    return <span>$ {(currency * q).toFixed(2)}</span>
  }

  return (
    <span
      className={cn(className, {
        'text-gray line-through text-[12.5px]': discount,
      })}
    >
      {formattedCurrency}
    </span>
  )
}
